<script setup lang="ts">
import { computed, defineAsyncComponent, nextTick, onMounted } from 'vue'
import type { Options } from '@splidejs/splide'
import {Splide, SplideSlide, SplideTrack} from '@splidejs/vue-splide'
import '@splidejs/vue-splide/css'

import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
const RenderAction = defineAsyncComponent(() => import("@/components/helpers/RenderAction.vue"))

type ShopvertizeHandle = {
	setCollapseIfNoContent: (shouldCollapse: boolean) => void
}

type ShopvertizeApi = {
	display: (placementId: string, size: string, elementId: string) => ShopvertizeHandle
	startServices: () => void
}

type HeroSlide = {
	image?: string
	callToAction?: string
	alt?: string
	placementId?: string
}

declare global {
	interface Window {
		shopvertag?: Array<(shopvertize: ShopvertizeApi) => void>
	}
}

const props = defineProps({
	slides: {
		type: Object
	},
	layout: {
		type: String,
		default: 'carousel'
	}
})

const shopvertizeSlideHeightClass = 'h-[600px]'
const heroCarouselId = `hero-shopvertize-${Math.random().toString(36).slice(2, 11)}`
const renderedPlacementSlideIndexes = new Set<number>()
const placementPositionMap = new Map<number, string>()
const isPlacementSlide = (slide?: HeroSlide) => Boolean(slide?.placementId?.trim())

const placementSlides = computed(() => {
	if (!props.slides?.slides) {
		return [] as Array<{slide: HeroSlide, index: number}>
	}

	return (props.slides.slides as HeroSlide[])
		.map((slide, index) => ({slide, index}))
		.filter(({slide}) => isPlacementSlide(slide))
})

const hasPlacementSlides = computed(() => placementSlides.value.length > 0)

const splideOptions = computed<Options>(() => ({
	type      : hasPlacementSlides.value ? 'slide' : 'loop',
	rewind    : hasPlacementSlides.value,
	perPage   : 1,
	perMove   : 1,
	arrows    : true,
	lazyLoad  : 'sequential',
	drag      : true,
	mediaQuery: 'min'
}))

const getPlacementContainerId = (slide: HeroSlide, index: number) => {
	const safePlacementId = (slide.placementId || 'placement').replace(/[^a-zA-Z0-9_-]/g, '-')

	return `${heroCarouselId}-${safePlacementId}-${index}`
}

const validatePlacementPosition = (slideIndex: number, expectedPlacementId: string) => {
	if (!props.slides?.slides) {
		return
	}

	const slide = (props.slides.slides as HeroSlide[])[slideIndex]
	if (!slide) {
		console.warn(`[Carousel] Slide ${slideIndex} does not exist in slides array`)
		return
	}

	const actualPlacementId = slide.placementId || ''
	placementPositionMap.set(slideIndex, actualPlacementId)

	if (actualPlacementId !== expectedPlacementId) {
		console.warn(
			`[Carousel] Placement ID mismatch at slide ${slideIndex}: ` +
			`expected '${expectedPlacementId}', but found '${actualPlacementId}'`
		)
	} else {
		console.log(`[Carousel] Slide ${slideIndex} validated: '${actualPlacementId}' ✓`)
	}
}

const renderShopvertizePlacement = (slideIndex: number) => {
	const placementSlide = placementSlides.value.find(({index}) => index === slideIndex)
	if (!placementSlide || renderedPlacementSlideIndexes.has(slideIndex)) {
		return
	}

	const {slide, index} = placementSlide
	const placementContainerId = getPlacementContainerId(slide, index)
	const placementContainer = document.getElementById(placementContainerId)
	if (!placementContainer) {
		console.warn(`[Carousel] Placement container not found for slide ${slideIndex} (id: ${placementContainerId})`)
		return
	}

	validatePlacementPosition(slideIndex, slide.placementId || '')

	window.shopvertag = window.shopvertag || []
	window.shopvertag.push((shopvertize) => {
		const handle = shopvertize.display(slide.placementId || '', '0x0', placementContainerId)
		handle.setCollapseIfNoContent(true)
		renderedPlacementSlideIndexes.add(slideIndex)
		console.log(`[Carousel] Rendered placement '${slide.placementId}' for slide ${slideIndex}`)
		shopvertize.startServices()
	})
}

const renderActiveShopvertizePlacement = async (slideIndex: number) => {
	if (!hasPlacementSlides.value || slideIndex < 0) {
		return
	}

	await nextTick()
	window.requestAnimationFrame(() => {
		renderShopvertizePlacement(slideIndex)
	})
}

const preloadAdjacentSlides = async (currentSlideIndex: number) => {
	if (!hasPlacementSlides.value) {
		return
	}

	const slidesArray = props.slides?.slides as HeroSlide[] | undefined
	if (!slidesArray) {
		return
	}

	const totalSlides = slidesArray.length
	const nextSlideIndex = (currentSlideIndex + 1) % totalSlides
	const prevSlideIndex = (currentSlideIndex - 1 + totalSlides) % totalSlides

	console.log(`[Carousel] Preloading adjacent slides: prev=${prevSlideIndex}, current=${currentSlideIndex}, next=${nextSlideIndex}`)

	await nextTick()
	window.requestAnimationFrame(() => {
		renderShopvertizePlacement(nextSlideIndex)
		renderShopvertizePlacement(prevSlideIndex)
	})
}

const handleSlideChange = (...args: unknown[]): void => {
	let nextSlideIndex: number | null = null
	for (const arg of args) {
		if (typeof arg === 'number') {
			nextSlideIndex = arg
			break
		}
	}

	if (nextSlideIndex === null || nextSlideIndex < 0) {
		return
	}

	renderActiveShopvertizePlacement(nextSlideIndex)
	preloadAdjacentSlides(nextSlideIndex)
}

onMounted(() => {
	renderActiveShopvertizePlacement(0)
	preloadAdjacentSlides(0)
})


</script>

<template>
	<div>
		<Splide
			:has-track="false"
			@splide:moved="handleSlideChange"
			:options="splideOptions">
			<SplideTrack>
				<SplideSlide v-for="(slide, index) in props.slides.slides" :key="`slide-${index}-${slide.placementId || slide.image || 'empty'}`">
					<div v-if="layout === 'hero'">
						<div v-if="slide.placementId" class="w-full rounded overflow-hidden" :class="shopvertizeSlideHeightClass">
							<div :id="getPlacementContainerId(slide, index)" class="w-full h-full"></div>
						</div>
						<a v-else-if="slide.callToAction" :href="slide.callToAction" class="block">
							<img
								:data-splide-lazy="`/Admin/Public/GetImage.ashx?Image=${slide.image}&Width=400&height=250&Format=webP&Quality=90&Crop=5&resolution=50`"
								:data-splide-lazy-srcset="`/Admin/Public/GetImage.ashx?Image=${slide.image}&Width=400&height=250&Format=webP&Quality=90&Crop=5&resolution=50 400w, /Admin/Public/GetImage.ashx?Image=${slide.image}&Width=800&height=500&Format=webP&Quality=90&Crop=5&resolution=50 800w, /Admin/Public/GetImage.ashx?Image=${slide.image}&Width=1600&height=1000&Format=webP&Quality=90&Crop=5&resolution=50 1600w`"
								sizes="100vw"
								width="736"
								height="460"
								:alt="slide.alt || ''"
								class="rounded object-cover object-center w-full h-[600px]" />
						</a>
						<img
							v-else
							:data-splide-lazy="`/Admin/Public/GetImage.ashx?Image=${slide.image}&Width=400&height=250&Format=webP&Quality=90&Crop=5&resolution=50`"
							:data-splide-lazy-srcset="`/Admin/Public/GetImage.ashx?Image=${slide.image}&Width=400&height=250&Format=webP&Quality=90&Crop=5&resolution=50 400w, /Admin/Public/GetImage.ashx?Image=${slide.image}&Width=800&height=500&Format=webP&Quality=90&Crop=5&resolution=50 800w, /Admin/Public/GetImage.ashx?Image=${slide.image}&Width=1600&height=1000&Format=webP&Quality=90&Crop=5&resolution=50 1600w`"
							sizes="100vw"
							width="736"
							height="460"
							:alt="slide.alt || ''"
							class="rounded object-cover object-center aspect-16/10 w-full h-[600px]" />
					</div>

					<div v-else >
						<div v-if="slide.placementId" class="w-full rounded overflow-hidden" :class="shopvertizeSlideHeightClass">
							<div :id="getPlacementContainerId(slide, index)" class="w-full h-full"></div>
						</div>
						<div v-else>
							<img
								:data-splide-lazy="`/Admin/Public/GetImage.ashx?Image=${slide.image}&Width=400&height=250&Format=webP&Quality=90&Crop=5&resolution=50`"
								:data-splide-lazy-srcset="`/Admin/Public/GetImage.ashx?Image=${slide.image}&Width=400&height=250&Format=webP&Quality=90&Crop=5&resolution=50 400w, /Admin/Public/GetImage.ashx?Image=${slide.image}&Width=800&height=500&Format=webP&Quality=90&Crop=5&resolution=50 800w, /Admin/Public/GetImage.ashx?Image=${slide.image}&Width=1600&height=1000&Format=webP&Quality=90&Crop=5&resolution=50 1600w`"
								sizes="100vw"
								:alt="slide.title"
								class="rounded w-full aspect-[16/10] object-cover object-center max-h-[600px]">


						</div>
						<div class="relative -mt-8 ml-6 mr-6 lg:-mt-40 grid grid-cols-1 lg:grid-cols-12" :class="slide.placementId !== '' ? 'invisible' : ''">
							<div class="p-8 lg:p-12 rounded flex flex-col items-start basis-1/2 lg:col-span-6 lg:col-start-6"
								 :class="'bg-' + slide.background">
								<div class="flex flex-col gap-y-4 mb-4" v-html="slide.content"></div>
								<RenderAction
									v-if="slide.callToAction && slide.callToActionTitle"
									action="link"
									theme="dark"
									:data="slide.callToAction">
									{{ slide.callToActionTitle }}
								</RenderAction>
							</div>
						</div>

					</div>
				</SplideSlide>
			</SplideTrack>

			<div class="splide__pagination relative hidden lg:flex flex-nowrap gap-x-2 justify-start -mt-12 p-0 bottom-0"></div>

			<div class="splide__arrows">
				<button class="splide__arrow splide__arrow--prev -left-4"><font-awesome-icon :icon="['fal', 'chevron-right']" /></button>
				<button class="splide__arrow splide__arrow--next -right-4"><font-awesome-icon :icon="['fal', 'chevron-right']" /></button>
			</div>
		</Splide>
	</div>
</template>

<style>
/* Overruling Splide defaults */
button.splide__pagination__page {
	@apply h-2 w-12 transition rounded-sm m-0 opacity-100 bg-grey hover:bg-grey-dark/90;
}
button.splide__pagination__page.is-active {
	@apply bg-grey-dark scale-100;
}
</style>
